import * as React from 'react'
import { ChevronDown, ChevronRight, Folder, FolderOpen, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import type { AssetFolder, FolderType } from '@/types'

interface FolderTreeProps {
  folders: AssetFolder[]
  selectedId: number | null
  onSelect: (id: number) => void
  onCreateFolder: (type: FolderType) => void
}

interface TreeNode {
  folder: AssetFolder
  children: TreeNode[]
  totalCount: number
}

function buildTree(folders: AssetFolder[]): TreeNode[] {
  const byId = new Map<number, TreeNode>()
  for (const f of folders) {
    byId.set(f.id, { folder: f, children: [], totalCount: f.asset_count })
  }

  const roots: TreeNode[] = []
  for (const node of byId.values()) {
    const pid = node.folder.parent_folder_id
    if (pid === null || !byId.has(pid)) {
      roots.push(node)
    } else {
      byId.get(pid)!.children.push(node)
    }
  }

  // Propagate counts upward (children first via post-order)
  function sumCounts(node: TreeNode): number {
    const childTotal = node.children.reduce((acc, c) => acc + sumCounts(c), 0)
    node.totalCount = node.folder.asset_count + childTotal
    return node.totalCount
  }
  roots.forEach(sumCounts)

  return roots
}

interface NodeRowProps {
  node: TreeNode
  depth: number
  selectedId: number | null
  defaultOpen?: boolean
  onSelect: (id: number) => void
}

function NodeRow({ node, depth, selectedId, defaultOpen = false, onSelect }: NodeRowProps) {
  const [open, setOpen] = React.useState(defaultOpen)
  const hasChildren = node.children.length > 0
  const isLeaf = !hasChildren
  const isActive = node.folder.id === selectedId

  const toggle = (e: React.MouseEvent) => {
    if (hasChildren) {
      e.stopPropagation()
      setOpen((v) => !v)
    }
  }

  const handleClick = () => {
    onSelect(node.folder.id)
    if (hasChildren) setOpen(true)
  }

  return (
    <>
      <li>
        <button
          onClick={handleClick}
          className={cn(
            'w-full flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm transition-colors text-left',
            isActive
              ? 'bg-accent text-accent-foreground'
              : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
            depth === 0 && 'font-semibold text-foreground',
          )}
          style={{ paddingLeft: `${0.5 + depth * 1}rem` }}
        >
          {/* Expand/collapse chevron */}
          {hasChildren ? (
            <span
              role="button"
              aria-label={open ? 'Collapse' : 'Expand'}
              onClick={toggle}
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              {open ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </span>
          ) : (
            <span className="shrink-0 w-3.5" aria-hidden="true" />
          )}

          {/* Folder icon */}
          {isLeaf ? (
            isActive ? (
              <FolderOpen className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <Folder className="h-3.5 w-3.5 shrink-0" />
            )
          ) : null}

          <span className="truncate flex-1">{node.folder.name}</span>
          <span className="text-xs tabular-nums text-muted-foreground shrink-0">
            {node.totalCount}
          </span>
        </button>
      </li>

      {hasChildren && open && (
        <>
          {node.children.map((child) => (
            <NodeRow
              key={child.folder.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              defaultOpen={false}
              onSelect={onSelect}
            />
          ))}
        </>
      )}
    </>
  )
}

export function FolderTree({ folders, selectedId, onSelect, onCreateFolder }: FolderTreeProps) {
  const roots = React.useMemo(() => buildTree(folders), [folders])

  return (
    <ScrollArea className="h-full">
      <div className="p-3 pb-2">
        {roots.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2 py-1">No folders yet</p>
        ) : (
          <ul className="space-y-0.5">
            {roots.map((node) => (
              <NodeRow
                key={node.folder.id}
                node={node}
                depth={0}
                selectedId={selectedId}
                defaultOpen
                onSelect={onSelect}
              />
            ))}
          </ul>
        )}

        <div className="mt-4 flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 h-7 text-xs text-muted-foreground justify-start"
            onClick={() => onCreateFolder('sku')}
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New folder
          </Button>
        </div>
      </div>
    </ScrollArea>
  )
}
